/**
 * ShippingPanel - Shipping workflow for packing station
 *
 * Displays after all items verified. Shows:
 * - "Print Shipping Label" (label-return provider) or "Mark Complete" button
 * - Loading state during API call
 * - Success state with tracking info and print/confirm buttons
 * - Error state with retry and manual fallback options
 *
 * Provider wording and the reprint path come from the shipping-provider
 * registry (`packing` copy keys + `reprint` mode), never from a branch on one
 * provider id, so StarShipIt, Portless and YunExpress each read their own.
 * Single-provider extras stay explicit: StarShipIt's dashboard links and
 * Portless's no-tracking cross-dock note.
 *
 * Hand over (a per-client provider at a shared location) has its own branch,
 * checked before pickup: no label and no manual complete. At an ordinary
 * station the packer packs it and gives it to the hand over bench
 * (completePacking). At the hand over bench the packer marks it handed over
 * (shipping.markHandedOver), which ships it with no label.
 *
 * WAREHOUSE UX:
 * - h-16 buttons (64px) for gloved hands
 * - Clear success/error states with Alert components
 * - ARIA live region for screen reader announcements
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { stripOrderNamePrefix } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  Loader2,
  Check,
  AlertCircle,
  Printer,
  RefreshCw,
  Package,
  ExternalLink,
  Copy,
  ClipboardCheck,
  X,
  Store,
  HandHelping,
} from 'lucide-react';
import {
  useSendToShipping,
  useConfirmShipment,
  useReprintPortlessLabel,
  useReprintShippingLabel,
  useHybridSkuSticker,
  useMarkHandedOver,
  type ShippingResult,
} from '../hooks/useShipping';
import {
  isLabelReturnProvider,
  SHIPPING_PROVIDER_PRESENTATION,
} from '@/features/production-settings/constants/shipping-providers';
import {
  handoverRefusalCopyKey,
  parseOrderOnHold,
  readCauseCode,
} from '@/lib/trpc-error-utils';
import { useCompletePacking } from '../hooks/usePackingMutation';
import { usePrintAgent } from '@/features/print-station/hooks/usePrintAgent';
import { LabelPrintDialog } from './LabelPrintDialog';
import { cn } from '@/lib/utils';
import type { RouterOutputs } from '@/lib/trpc';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { useHotkeySettings } from '@/features/production-settings/hotkeys/useHotkeySettings';
import { matchesCombo, isFocusOnInput } from '@/features/production-settings/hotkeys/useHotkeyEngine';
import { formatBindingLabel } from '@/features/production-settings/hotkeys/hotkey-types';

// Calculate announcement duration based on message length (50ms per char, min 3s)
const getAnnouncementDuration = (message: string): number =>
  Math.max(3000, message.length * 50);

/** Inferred provider union (manual | starshipit | yunexpress | portless | handover). */
type ShippingProvider = RouterOutputs['shipping']['getShippingSettings']['shippingProvider'];

interface ShippingPanelProps {
  orderId: string;
  locationId: string;
  orderName: string;
  /** Resolved per-location provider. Drives the dispatch/UI branch. */
  shippingProvider: ShippingProvider;
  hasCredentials: boolean;
  allItemsVerified: boolean;
  isPickup?: boolean;
  /** BLU packing hard-block: the order's production_status is
   *  EXTERNALLY_FULFILLED (Shopify sync picked up a fulfillment from outside
   *  the app). Pre-flight — blocks every ship action before it fires, ahead
   *  of confirmShipment's own idempotency no-op. */
  isExternallyFulfilled?: boolean;
  isCompletingManual?: boolean;
  onShipmentComplete: (result: { manual: boolean }) => void;
  onPrintDialogChange?: (open: boolean) => void;
  /** The order turned out to be paused (on hold, or pulled for missing stock):
   *  Complete Packing refused it before any label was bought. The station shows
   *  its own amber on-hold modal (with the beep); the panel returns to ready. */
  onOrderOnHold: (holdReason: string | null) => void;
  /**
   * The order is already packed (PACKED or SHIPPED). For a Hand over order
   * this is the waiting state: any packer at any station hands it over.
   */
  isAlreadyPacked?: boolean;
  /**
   * A Hand over order left this station: packed and sent to the hand over
   * bench, or handed over to the client.
   */
  onHandover?: (outcome: HandoverOutcome) => void;
}

export type HandoverOutcome = 'sent_to_bench' | 'handed_over' | 'already_handed_over';

type PanelState = 'ready' | 'loading' | 'success' | 'error';

/**
 * Categorise shipping-provider errors so we can show the right guidance.
 * "not ready" is the Portless pull-model case (label not generated yet) —
 * transient: the packer retries once the order has synced to Portless.
 */
function categoriseError(message: string): 'config' | 'validation' | 'transient' {
  const lower = message.toLowerCase();
  if (
    lower.includes('credentials') ||
    lower.includes('forbidden') ||
    lower.includes('unauthorized') ||
    lower.includes('api key') ||
    lower.includes('subscription key')
  ) {
    return 'config';
  }
  if (
    lower.includes('timeout') ||
    lower.includes('network') ||
    lower.includes('econnrefused') ||
    lower.includes('server error') ||
    lower.includes('not ready') ||
    lower.includes('503') ||
    lower.includes('502') ||
    lower.includes('504')
  ) {
    return 'transient';
  }
  return 'validation';
}

function getErrorGuidance(
  category: ReturnType<typeof categoriseError>,
  t: (key: string) => string,
  /** The provider's own `packing.checkCredentials` key; null for Manual. */
  checkCredentialsKey: string | null,
  /**
   * StarShipIt failures are kept out of the Issues queue (fixed in StarShipIt
   * itself), so a validation error must not promise a coordinator review.
   */
  isStarShipIt: boolean
): string {
  const map: Record<ReturnType<typeof categoriseError>, string> = {
    // Provider-aware via the registry: a Portless or YunExpress credential
    // failure must not tell the packer to check StarShipIt credentials.
    config: checkCredentialsKey ? t(checkCredentialsKey) : t('shipping.coordinatorWillReview'),
    validation: isStarShipIt
      ? t('shipping.fixInStarshipitGuidance')
      : t('shipping.coordinatorWillReview'),
    transient: t('shipping.temporaryIssue'),
  };
  return map[category];
}

/**
 * YunExpress failure codes (`result.errorCode` from the send path) that have
 * a `shipping.yunexpress.errors.<code>` sentence, plus NO_WAYBILL, which a
 * reprint refusal maps onto. Each sentence says what happened AND what to do,
 * so the generic guidance line is not shown under it. A code outside this set
 * (a backend newer than this bundle) shows the backend's own message.
 */
const YUNEXPRESS_ERROR_CODES: ReadonlySet<string> = new Set([
  'MISSING_WEIGHT',
  'MISSING_PRICE',
  'ADDRESS_INCOMPLETE',
  'NO_SHIPPING_PRODUCT',
  'NO_SHIPPABLE_ITEMS',
  'NO_CREDENTIALS',
  'PURCHASE_IN_PROGRESS',
  'CANCEL_PENDING',
  'CLAIM_LOST',
  'CREATE_UNCONFIRMED',
  'CARRIER_REJECTED',
  'CARRIER_UNAVAILABLE',
  'LABEL_UNAVAILABLE',
  'SAVE_FAILED',
  'NO_WAYBILL',
]);

/**
 * `shipping.reprintShippingLabel` refusals (forwarded `cause.code`) and the
 * YunExpress error sentence each one reuses.
 */
const YUNEXPRESS_REPRINT_CAUSES: Readonly<Record<string, string>> = {
  YUNEXPRESS_NO_WAYBILL: 'NO_WAYBILL',
  YUNEXPRESS_NO_CREDENTIALS: 'NO_CREDENTIALS',
};

/** A send failure the backend classified (`result.errorCode` + params). */
interface CodedShippingError {
  code: string;
  params: Record<string, string> | null;
}

/**
 * The sentence key for a classified failure. Every code reads
 * `shipping.yunexpress.errors.<code>`, except MISSING_WEIGHT, whose advice
 * depends on what the backend could name:
 *   - no `productType`: the SKU carries no product type code, so no default
 *     weight can ever match it and the only fix is a weight on the product in
 *     Shopify. Telling the admin to add a default would send them in a circle.
 *   - no `clientName`: the sentence drops the "under <client>" clause rather
 *     than render an empty name.
 */
function yunExpressErrorKey(coded: CodedShippingError): string {
  if (coded.code !== 'MISSING_WEIGHT') return `shipping.yunexpress.errors.${coded.code}`;
  const hasType = Boolean(coded.params?.productType?.trim());
  const hasClient = Boolean(coded.params?.clientName?.trim());
  if (!hasType) {
    return hasClient
      ? 'shipping.yunexpress.errors.MISSING_WEIGHT_NO_TYPE'
      : 'shipping.yunexpress.errors.MISSING_WEIGHT_NO_TYPE_NO_CLIENT';
  }
  return hasClient
    ? 'shipping.yunexpress.errors.MISSING_WEIGHT'
    : 'shipping.yunexpress.errors.MISSING_WEIGHT_NO_CLIENT';
}

/**
 * Operator copy for a classified YunExpress failure. `detail` is the
 * carrier's own (redacted, capped) reason, shown as muted secondary text only
 * where the sentence cannot carry it: a CARRIER_REJECTED refusal, or a code
 * this bundle does not know yet.
 */
function describeYunExpressError(
  translate: (key: string, params: Record<string, string>) => string,
  coded: CodedShippingError,
  fallback: string
): { message: string; detail: string | null } {
  const known = YUNEXPRESS_ERROR_CODES.has(coded.code);
  const message = known
    ? translate(yunExpressErrorKey(coded), { ...(coded.params ?? {}) })
    : fallback;
  const detail =
    coded.code === 'CARRIER_REJECTED' || !known ? coded.params?.detail?.trim() || null : null;
  return { message, detail };
}

/** Build StarShipIt dashboard search URL for an order. */
function starshipitUrl(orderName: string): string {
  return `https://app2.starshipit.com/search/${encodeURIComponent(stripOrderNamePrefix(orderName))}`;
}

export function ShippingPanel({
  orderId,
  locationId,
  orderName,
  shippingProvider,
  hasCredentials,
  allItemsVerified,
  isPickup = false,
  isExternallyFulfilled = false,
  isCompletingManual = false,
  onShipmentComplete,
  onPrintDialogChange,
  onOrderOnHold,
  isAlreadyPacked = false,
  onHandover,
}: ShippingPanelProps) {
  const { t } = useTranslation('production');
  const [panelState, setPanelState] = useState<PanelState>('ready');
  const [shippingResult, setShippingResult] = useState<ShippingResult | null>(
    null
  );
  const [alreadyShipped, setAlreadyShipped] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [codedError, setCodedError] = useState<CodedShippingError | null>(null);
  const [providerErrors, setProviderErrors] = useState<string[] | null>(null);
  const [failureQueued, setFailureQueued] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [labelPrinted, setLabelPrinted] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  const [copied, setCopied] = useState(false);

  // Phase 51: StarShipIt, Portless and YunExpress are label-return API
  // providers — the panel's state machine, audio, label-print, and
  // success/error handling are identical; only the dispatched adapter
  // (backend), the copy and the reprint path differ. All three come from the
  // registry row, so the next label-return provider is a registry-row change,
  // not a code hunt.
  const presentation = SHIPPING_PROVIDER_PRESENTATION[shippingProvider];
  const packingCopy = presentation?.isLabelReturn ? presentation.packing : null;
  const reprintMode = presentation?.isLabelReturn ? presentation.reprint : null;
  // Single-provider extras, deliberately not registry-driven: StarShipIt's
  // dashboard links, and Portless's cross-dock label that has no tracking yet.
  const isStarShipIt = shippingProvider === 'starshipit';
  const isPortless = shippingProvider === 'portless';
  const isLabelProvider = isLabelReturnProvider(shippingProvider);
  const canUseLabelProvider = isLabelProvider && hasCredentials;

  // When true, closing the print dialog auto-triggers handleConfirmShipment
  const confirmAfterPrintRef = useRef(false);

  const sendMutation = useSendToShipping();
  const confirmMutation = useConfirmShipment();
  const reprintPortlessMutation = useReprintPortlessLabel();
  const reprintApiMutation = useReprintShippingLabel();
  const reprintPending = reprintPortlessMutation.isPending || reprintApiMutation.isPending;
  const hybridStickerMutation = useHybridSkuSticker();
  const completePackingMutation = useCompletePacking();
  const markHandedOverMutation = useMarkHandedOver();
  const { printLabel: agentPrintLabel } = usePrintAgent();
  const isHandover = shippingProvider === 'handover';
  const [handoverError, setHandoverError] = useState<string | null>(null);

  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const announcementTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // BLU-281: Synchronous guard to prevent double-invocation from rapid Enter keystrokes
  const isSubmittingRef = useRef(false);

  // Cleanup announcement timeout on unmount
  useEffect(() => {
    return () => {
      if (announcementTimeoutRef.current) {
        clearTimeout(announcementTimeoutRef.current);
      }
    };
  }, []);

  // Announce state changes for screen readers
  const announce = useCallback((message: string) => {
    // Clear previous timeout to prevent overlapping announcements
    if (announcementTimeoutRef.current) {
      clearTimeout(announcementTimeoutRef.current);
    }
    setAnnouncement(message);
    // Clear after announcement duration (longer for longer messages)
    announcementTimeoutRef.current = setTimeout(() => {
      setAnnouncement('');
      announcementTimeoutRef.current = null;
    }, getAnnouncementDuration(message));
  }, []);

  // BLU-1294: Complete Packing refused a paused order (on hold, or pulled for
  // missing stock) before any label was bought. Nothing happened, so the panel
  // goes back to ready and the station's own amber on-hold modal (with its
  // beep) tells the packer to set the parcel aside. No red "Shipping failed"
  // card and no "Try again": retrying cannot help a paused order.
  const handOffOrderOnHold = useCallback((holdReason: string | null) => {
    setErrorMessage(null);
    setCodedError(null);
    setProviderErrors(null);
    setFailureQueued(false);
    setPanelState('ready');
    onOrderOnHold(holdReason);
  }, [onOrderOnHold]);

  // BLU-281: Reset panel state when order changes
  useEffect(() => {
    setPanelState('ready');
    setShippingResult(null);
    setAlreadyShipped(false);
    setErrorMessage(null);
    setCodedError(null);
    setProviderErrors(null);
    setFailureQueued(false);
    setShowPrintDialog(false);
    setLabelPrinted(false);
    setHandoverError(null);
    confirmAfterPrintRef.current = false;
    isSubmittingRef.current = false;
  }, [orderId]);

  // Notify parent when print dialog opens/closes (for auto-focus pause)
  useEffect(() => {
    onPrintDialogChange?.(showPrintDialog);
  }, [showPrintDialog, onPrintDialogChange]);

  // Handle send to shipping (provider-agnostic dispatcher — StarShipIt | Portless | YunExpress)
  const handleSendToShipping = useCallback(async () => {
    // Pre-flight hard block: never fire a label-return send for an order
    // already fulfilled outside the app — confirmShipment's idempotency
    // no-op only guards the LATER confirm step, not this one. The render
    // below already hides the button; this is defense in depth.
    if (isExternallyFulfilled) return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    // Spoken before every failure below ("Shipping failed: ...").
    const failedPrefix = t('shipping.shippingFailed');

    setPanelState('loading');
    setErrorMessage(null);
    setCodedError(null);
    setProviderErrors(null);
    setFailureQueued(false);
    setAlreadyShipped(false);
    announce(t(packingCopy?.sending ?? 'shipping.generatingLabel'));

    try {
      const result = await sendMutation.mutateAsync({ orderId, locationId });

      if (result.alreadyExternallyFulfilled) {
        // Race: order became EXTERNALLY_FULFILLED between load and send —
        // the label-return guard short-circuited before calling the
        // provider, so nothing was printed or shipped. Show the same notice
        // as the pre-flight hard block instead of the success card; do NOT
        // proceed to the shipped state. useSendToShipping's onSuccess already
        // invalidated getPackingOrder, so the next render flips
        // isExternallyFulfilled and locks the panel via the block above.
        toast.error(t('shipping.orderExternallyFulfilled'));
        announce(t('shipping.orderExternallyFulfilled'));
        setPanelState('ready');
        return;
      }

      if (result.success) {
        const resultData = {
          // Nullable for Portless: the cross-dock label usually has no
          // tracking at pull time.
          trackingNumber: result.trackingNumber,
          carrier: result.carrier,
          carrierName: result.carrierName,
          labelBase64: result.labelBase64,
          labelType: result.labelType,
        };

        if (result.alreadyShipped) {
          announce(`${t('shipping.labelAlreadyGenerated')} ${result.trackingNumber ?? ''}`);
        } else {
          announce(`${t('shipping.labelGenerated')} ${result.trackingNumber ?? ''}`);
        }

        // BLU-282: For label-return providers, the backend auto-ships on label
        // generation/pull. Try auto-print first. If print succeeds AND a label
        // provider is active, skip the success state entirely and move on.
        let autoPrintSucceeded = false;
        if (result.labelBase64) {
          const VALID_FORMATS = ['PDF', 'ZPL', 'PNG', 'JPG', 'JPEG'];
          const rawFormat = (result.labelType || 'PDF').toUpperCase();
          const format = VALID_FORMATS.includes(rawFormat) ? rawFormat : 'PDF';

          const printResult = await agentPrintLabel({
            data: result.labelBase64,
            format,
          });

          if (printResult.success) {
            toast.success(t('shipping.labelSentToPrinter'));
            setLabelPrinted(true);
            autoPrintSucceeded = true;

            // PR 2: Hybrid SKU sticker — Portless only, printed AFTER the label.
            // STRICTLY non-fatal: the backend already shipped this order, so a
            // sticker fetch/print failure must NEVER reach the outer catch (which
            // would flip the panel to an "error" state). The fetch is locally
            // guarded; the agent print is best-effort.
            if (result.provider === 'portless') {
              try {
                const sticker = await hybridStickerMutation
                  .mutateAsync({ orderId, locationId })
                  .catch(() => null);
                if (sticker?.isHybrid) {
                  const stickerPrint = await agentPrintLabel({ data: sticker.stickerBase64, format: 'PDF' });
                  if (!stickerPrint.success) {
                    // Persist (no auto-dismiss): on a scanner station the operator
                    // glances away, and a missed sticker ships an unlabeled bag.
                    toast.error(t('shipping.hybridStickerPrintFailed'), { duration: Infinity });
                    console.warn('[HybridSticker] print failed:', stickerPrint.error);
                  }
                }
              } catch (err) {
                toast.error(t('shipping.hybridStickerFailed'), { duration: Infinity });
                console.warn('[HybridSticker] error:', err);
                // Surface generate-failure rates in prod (the print path is
                // already captured inside usePrintAgent).
                Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
                  tags: { feature: 'hybrid-sticker' },
                  extra: { orderId, locationId },
                });
              }
            }

            // BLU-282: backend already auto-shipped — skip success state, move on
            if (canUseLabelProvider) {
              onShipmentComplete({ manual: false });
              return;
            }
          } else {
            console.warn('[PrintAgent] Auto-print failed, showing dialog:', printResult.error);
          }
        }

        // Show success state: either auto-print failed, no label data, or non-StarShipIt flow
        setShippingResult(resultData);
        setAlreadyShipped(result.alreadyShipped);
        setPanelState('success');

        // Open print dialog if auto-print failed and we have label data
        if (result.labelBase64 && !autoPrintSucceeded) {
          setShowPrintDialog(true);
        }
      } else {
        const errMsg = result.errorMessage || 'Failed to generate label';
        // YunExpress classifies its failures (`errorCode` + params) so the
        // packer reads a translated sentence instead of the carrier's English.
        const coded: CodedShippingError | null =
          result.provider === 'yunexpress' && result.errorCode
            ? { code: result.errorCode, params: result.errorParams ?? null }
            : null;
        setErrorMessage(errMsg);
        setCodedError(coded);
        setProviderErrors(result.providerErrors ?? null);
        setFailureQueued(result.failureQueued);
        setPanelState('error');

        const spoken = coded
          ? describeYunExpressError((key, params) => t(key, params), coded, errMsg).message
          : errMsg;
        const suffix = result.failureQueued
          ? `. ${t('shipping.queuedForCoordinator')}`
          : '';
        announce(`${failedPrefix} ${spoken}${suffix}`);
      }
    } catch (error) {
      const onHold = parseOrderOnHold(error);
      if (onHold.isOnHold) {
        handOffOrderOnHold(onHold.holdReason);
        return;
      }
      const refusalKey = handoverRefusalCopyKey(error);
      const message = refusalKey ? t(refusalKey) : error instanceof Error ? error.message : 'Unknown error';
      setErrorMessage(message);
      setCodedError(null);
      setFailureQueued(false);
      setPanelState('error');
      announce(`${failedPrefix} ${message}`);
    } finally {
      isSubmittingRef.current = false;
    }
  }, [orderId, locationId, sendMutation, announce, agentPrintLabel, hybridStickerMutation, canUseLabelProvider, packingCopy, onShipmentComplete, t, isExternallyFulfilled, handOffOrderOnHold]);

  // Handle confirm shipment
  const handleConfirmShipment = useCallback(async () => {
    if (!shippingResult?.trackingNumber) {
      toast.error(t('shipping.noTrackingNumber'));
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setErrorMessage(null);
    setPanelState('loading');
    announce(t('shipping.confirmingShipment'));

    try {
      const result = await confirmMutation.mutateAsync({
        orderId,
        locationId,
        trackingNumber: shippingResult.trackingNumber,
        carrier: shippingResult.carrier || 'Unknown',
      });

      // Backend race: order became EXTERNALLY_FULFILLED between load and
      // confirm (e.g. another channel shipped it first). confirmShipment
      // stays idempotent-success (never throws) but flags it — show the
      // same "fulfilled outside the app" notice instead of a success toast.
      if (result.alreadyExternallyFulfilled) {
        toast.error(t('shipping.orderExternallyFulfilled'));
        announce(t('shipping.orderExternallyFulfilled'));
      } else {
        toast.success(t('shipping.orderMarkedShipped'));
        announce(t('shipping.shipmentConfirmed'));
      }
      onShipmentComplete({ manual: false });
    } catch (error) {
      const refusalKey = handoverRefusalCopyKey(error);
      const message = refusalKey ? t(refusalKey) : error instanceof Error ? error.message : 'Failed to confirm';
      setErrorMessage(message);
      setPanelState('success'); // Stay on success view so user can retry or close
      toast.error(`${t('shipping.failedToMarkShipped')} ${message}`);
      announce(`${t('shipping.confirmationFailed')} ${message}`);
    } finally {
      isSubmittingRef.current = false;
    }
  }, [
    orderId,
    locationId,
    shippingResult,
    confirmMutation,
    onShipmentComplete,
    announce,
    t,
  ]);

  // Handle "Mark as Shipped" click — if label not yet printed, open print dialog first; otherwise confirm directly
  const handleMarkAsShipped = useCallback(() => {
    // Null-tracking label-provider order (Portless cross-dock): the backend
    // already shipped it and confirmShipment requires a tracking number —
    // dismiss directly instead of routing into handleConfirmShipment's
    // misleading "no tracking" error.
    if (!shippingResult?.trackingNumber && canUseLabelProvider) {
      onShipmentComplete({ manual: false });
      return;
    }
    if (shippingResult?.labelBase64 && !labelPrinted) {
      confirmAfterPrintRef.current = true;
      setShowPrintDialog(true);
    } else {
      handleConfirmShipment();
    }
  }, [shippingResult, labelPrinted, handleConfirmShipment, canUseLabelProvider, onShipmentComplete]);

  // Handle manual complete - marks order as PACKED only (not SHIPPED).
  // PackingPage shows completion overlay, user clicks "Done" to mark SHIPPED.
  const handleManualComplete = useCallback(async () => {
    // Pre-flight hard block — see handleSendToShipping.
    if (isExternallyFulfilled) return;
    setPanelState('loading');
    announce(t('shipping.markingPacked'));

    try {
      await completePackingMutation.mutateAsync({ orderId, locationId });
      announce(t('shipping.orderPacked'));
      setPanelState('ready');
      onShipmentComplete({ manual: true });
    } catch (error) {
      const onHold = parseOrderOnHold(error);
      if (onHold.isOnHold) {
        handOffOrderOnHold(onHold.holdReason);
        return;
      }
      const message =
        error instanceof Error ? error.message : 'Failed to complete';
      setErrorMessage(message);
      // This failure is the manual completion's own, not an earlier coded
      // label failure the packer fell back from.
      setCodedError(null);
      setPanelState('error');
      announce(`${t('shipping.failedToMarkComplete')} ${message}`);
    }
  }, [orderId, locationId, completePackingMutation, onShipmentComplete, announce, t, isExternallyFulfilled, handOffOrderOnHold]);

  // Hand over: plain copy for a refusal, else the backend's message.
  const describeHandoverFailure = useCallback((error: unknown): string => {
    const key = handoverRefusalCopyKey(error);
    if (key) return t(key);
    return error instanceof Error && error.message ? error.message : t('shipping.handover.failed');
  }, [t]);

  // Pack it and send it to the hand over bench. No label, no completion
  // overlay; whoever scans it when the client collects marks it handed over.
  const handleGiveToHandoverBench = useCallback(async () => {
    if (isExternallyFulfilled || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setHandoverError(null);
    announce(t('shipping.markingPacked'));
    try {
      await completePackingMutation.mutateAsync({ orderId, locationId });
      announce(t('shipping.handover.sentToBench', { orderName }));
      onHandover?.('sent_to_bench');
    } catch (error) {
      const onHold = parseOrderOnHold(error);
      if (onHold.isOnHold) {
        handOffOrderOnHold(onHold.holdReason);
        return;
      }
      const message = describeHandoverFailure(error);
      setHandoverError(message);
      announce(message);
    } finally {
      isSubmittingRef.current = false;
    }
  }, [isExternallyFulfilled, announce, t, completePackingMutation, orderId, locationId, orderName, onHandover, handOffOrderOnHold, describeHandoverFailure]);

  // The client has the order (any packer, any station). Ships it with no
  // label; the backend packs it first if needed and is a no-op on a retry.
  const handleHandedOver = useCallback(async () => {
    if (isExternallyFulfilled || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setHandoverError(null);
    try {
      const result = await markHandedOverMutation.mutateAsync({ orderId, locationId });
      announce(t('shipping.handover.handedOverDone', { orderName }));
      onHandover?.(result.alreadyHandedOver ? 'already_handed_over' : 'handed_over');
    } catch (error) {
      const onHold = parseOrderOnHold(error);
      if (onHold.isOnHold) {
        handOffOrderOnHold(onHold.holdReason);
        return;
      }
      const message = describeHandoverFailure(error);
      setHandoverError(message);
      announce(message);
    } finally {
      isSubmittingRef.current = false;
    }
  }, [isExternallyFulfilled, markHandedOverMutation, orderId, locationId, announce, t, orderName, onHandover, handOffOrderOnHold, describeHandoverFailure]);

  // Fetch the already-bought label again and print it (printer-jam recovery,
  // or an already-shipped re-entry with no label in hand). The registry's
  // `reprint` mode picks the endpoint: Portless re-pulls its cross-dock PDF,
  // YunExpress re-downloads the stored waybill's label. Both are read-only on
  // the backend — order status and tracking are untouched.
  //
  // A refusal always leads with a sentence in the packer's language: the
  // mapped cause code's own sentence, else the generic "could not be printed
  // again". Whatever the backend said in English rides underneath as the
  // toast's muted description, so nothing is lost for whoever investigates.
  // Sticky (as for the hybrid sticker above): the packer is looking at the
  // printer, not the screen, when a reprint fails.
  const handleReprint = useCallback(async () => {
    const reprintRefused = (message: string, rawDetail?: string | null) => {
      const detail = rawDetail?.trim();
      toast.error(message, {
        ...(detail && detail !== message ? { description: detail } : {}),
        duration: Infinity,
      });
    };
    try {
      const result =
        reprintMode === 'api'
          ? await reprintApiMutation.mutateAsync({ orderId, locationId })
          : await reprintPortlessMutation.mutateAsync({ orderId, locationId });
      const labelBase64 = result.labelBase64;
      if (result.success && labelBase64) {
        setShippingResult((prev) => ({
          trackingNumber: prev?.trackingNumber ?? result.trackingNumber,
          carrier: prev?.carrier ?? null,
          carrierName: prev?.carrierName ?? null,
          labelBase64,
          labelType: result.labelType,
        }));
        const printResult = await agentPrintLabel({
          data: labelBase64,
          format: result.labelType || 'PDF',
        });
        if (printResult.success) {
          toast.success(t('shipping.labelSentToPrinter'));
          setLabelPrinted(true);
        } else {
          setShowPrintDialog(true);
        }
      } else {
        reprintRefused(t('shipping.reprintFailed'), result.errorMessage);
      }
    } catch (error) {
      // A YunExpress refusal is a forwarded cause code with its own sentence;
      // the backend's English message is then redundant and is not shown.
      const cause = readCauseCode(error);
      const mapped = cause ? YUNEXPRESS_REPRINT_CAUSES[cause] : undefined;
      if (mapped) {
        reprintRefused(t(`shipping.yunexpress.errors.${mapped}`));
      } else {
        reprintRefused(t('shipping.reprintFailed'), error instanceof Error ? error.message : null);
      }
    }
  }, [orderId, locationId, reprintMode, reprintApiMutation, reprintPortlessMutation, agentPrintLabel, t]);

  // Copy order number to clipboard
  const handleCopyOrderNumber = useCallback(() => {
    const cleanName = stripOrderNamePrefix(orderName);
    navigator.clipboard.writeText(cleanName).then(() => {
      setCopied(true);
      toast.success(`${t('shipping.copied')} ${cleanName}`);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [orderName, t]);

  // BLU-286: Don't auto-focus the confirm button on success state transition.
  // USB scanners send Enter after every scan, which activates whatever button has
  // focus via native browser behavior. The shipping-phase scanner input (added in
  // PackingPage) captures scanner keystrokes instead. Users can still click buttons
  // with mouse or use Ctrl+Enter/Ctrl+L hotkeys.
  useEffect(() => {
    // BLU-281: Reset submission guard on mount
    isSubmittingRef.current = false;
  }, []);

  // Hotkeys for success state: packer_confirm + packer_label
  // Reads configured bindings from useHotkeySettings (BLU-289).
  // Keeps own useEffect (not useHotkeyEngine) to avoid dual-engine coexistence with PackingPage.
  // Supports both combo (modifier+key) and hold (key held for N ms) bindings.
  //
  // NOTE: packer_confirm and packer_label MUST have different bindings.
  // They are both active simultaneously in the success state, so sharing
  // a binding would cause unpredictable behavior. The settings UI enforces
  // within-station uniqueness at save time.
  const { hotkeyMap } = useHotkeySettings();
  const confirmBinding = hotkeyMap.packer_confirm;
  const labelBinding = hotkeyMap.packer_label;

  useEffect(() => {
    if (panelState !== 'success') return;

    // Hold detection state
    const holdStarts = new Map<string, number>();

    const fireConfirm = () => {
      // BLU-282: In label-provider mode, confirmButtonRef is null (no Mark as Shipped button).
      // Hotkey dismisses and moves on (order already shipped on backend).
      if (confirmButtonRef.current) {
        confirmButtonRef.current.click();
      } else if (canUseLabelProvider) {
        onShipmentComplete({ manual: false });
      }
    };

    const fireLabel = () => {
      const printBtn = document.querySelector<HTMLButtonElement>('[data-packing-print-label]:not(:disabled)');
      if (printBtn) printBtn.click();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFocusOnInput()) return;

      // Combo bindings fire on keydown
      if (confirmBinding?.type === 'combo' && matchesCombo(e, confirmBinding)) {
        e.preventDefault();
        e.stopPropagation();
        fireConfirm();
        return;
      }
      if (labelBinding?.type === 'combo' && matchesCombo(e, labelBinding)) {
        e.preventDefault();
        e.stopPropagation();
        fireLabel();
        return;
      }

      // Hold bindings: start tracking on keydown (ignore key repeat)
      if (e.repeat) return;
      const holdKey = e.key;
      if (confirmBinding?.type === 'hold' && confirmBinding.key.toLowerCase() === holdKey.toLowerCase()) {
        if (!holdStarts.has('confirm')) holdStarts.set('confirm', Date.now());
      }
      if (labelBinding?.type === 'hold' && labelBinding.key.toLowerCase() === holdKey.toLowerCase()) {
        if (!holdStarts.has('label')) holdStarts.set('label', Date.now());
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const holdKey = e.key;

      // Check confirm hold
      if (confirmBinding?.type === 'hold' && confirmBinding.key.toLowerCase() === holdKey.toLowerCase()) {
        const start = holdStarts.get('confirm');
        holdStarts.delete('confirm');
        if (start && !isFocusOnInput() && (Date.now() - start) >= confirmBinding.durationMs) {
          e.preventDefault();
          fireConfirm();
          return;
        }
      }

      // Check label hold
      if (labelBinding?.type === 'hold' && labelBinding.key.toLowerCase() === holdKey.toLowerCase()) {
        const start = holdStarts.get('label');
        holdStarts.delete('label');
        if (start && !isFocusOnInput() && (Date.now() - start) >= labelBinding.durationMs) {
          e.preventDefault();
          fireLabel();
        }
      }
    };

    const handleBlur = () => holdStarts.clear();

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('blur', handleBlur);
    };
  }, [panelState, canUseLabelProvider, onShipmentComplete, confirmBinding, labelBinding]);


  // Render different states
  const renderContent = () => {
    // Hard block — the order was already fulfilled outside the app. Replaces
    // every action (send/manual-complete/mark-shipped) with a notice rather
    // than disabling buttons individually, since a fresh scan can land here
    // straight from 'ready'.
    if (isExternallyFulfilled) {
      return (
        <Alert variant="destructive" role="alert">
          <AlertCircle className="h-5 w-5" aria-hidden="true" />
          <AlertDescription>{t('shipping.orderExternallyFulfilled')}</AlertDescription>
        </Alert>
      );
    }

    // Hand over, checked before pickup: no label and no manual complete in
    // either place, so it never reaches the panel's label/manual states.
    if (isHandover) {
      const pending = completePackingMutation.isPending || markHandedOverMutation.isPending;
      // Packed and waiting: the primary action hands it over. Not packed yet:
      // the primary sends it to the bench, and a smaller second action hands
      // it over straight away when the client is standing there.
      const primaryLabel = isAlreadyPacked
        ? t('shipping.handover.handedOver')
        : t('shipping.handover.giveToBench');
      return (
        <div className="space-y-3">
          <p className="flex items-center gap-2 rounded-md border border-teal-300 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-800 dark:border-teal-700 dark:bg-teal-950/30 dark:text-teal-200">
            <HandHelping className="h-5 w-5 shrink-0" aria-hidden="true" />
            {isAlreadyPacked ? t('shipping.handover.benchNote') : t('shipping.handover.stationNote')}
          </p>
          <Button
            data-packing-primary
            onClick={isAlreadyPacked ? handleHandedOver : handleGiveToHandoverBench}
            disabled={!allItemsVerified || pending}
            className="w-full h-16 text-lg font-semibold gap-3 bg-teal-600 hover:bg-teal-700 text-white"
            size="lg"
            aria-busy={pending}
          >
            {pending ? (
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
            ) : (
              <HandHelping className="h-6 w-6" aria-hidden="true" />
            )}
            {primaryLabel}
            {!pending && (
              <kbd className="ml-1 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_ship)}</kbd>
            )}
          </Button>
          {!isAlreadyPacked && (
            <Button
              variant="outline"
              onClick={handleHandedOver}
              disabled={!allItemsVerified || pending}
              className="w-full h-12 text-base font-medium gap-2 border-teal-300 text-teal-800 hover:bg-teal-50 dark:border-teal-700 dark:text-teal-200 dark:hover:bg-teal-950/30"
            >
              {t('shipping.handover.handedOverNow')}
            </Button>
          )}
          {!allItemsVerified && (
            <p className="text-sm text-muted-foreground text-center">
              {t('shipping.handover.verifyFirst')}
            </p>
          )}
          {handoverError && (
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-5 w-5" aria-hidden="true" />
              <AlertDescription>{handoverError}</AlertDescription>
            </Alert>
          )}
        </div>
      );
    }

    switch (panelState) {
      case 'ready':
        // Pickup orders: single "Mark Ready for Pickup" button + Copy Order
        if (isPickup) {
          return (
            <div className="space-y-3">
              <Button
                data-packing-primary
                onClick={handleManualComplete}
                disabled={!allItemsVerified || isCompletingManual}
                className="w-full h-16 text-lg font-semibold gap-3 bg-amber-600 hover:bg-amber-700 text-white"
                size="lg"
                aria-busy={isCompletingManual}
                aria-label={
                  isCompletingManual
                    ? t('shipping.scanningDots')
                    : allItemsVerified
                      ? `${t('shipping.markReadyPickup')} (Enter)`
                      : `${t('shipping.markReadyPickup')} - ${t('shipping.verifyBeforeReady')}`
                }
              >
                {isCompletingManual ? (
                  <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
                ) : (
                  <Store className="h-6 w-6" aria-hidden="true" />
                )}
                {isCompletingManual ? t('shipping.completing') : t('shipping.markReadyPickup')}
                {!isCompletingManual && (
                  <kbd className="ml-1 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_ship)}</kbd>
                )}
              </Button>

              <Button
                data-packing-copy
                onClick={handleCopyOrderNumber}
                variant="outline"
                className="w-full h-12 text-base font-medium gap-2"
                size="lg"
                aria-label={`${t('shipping.copyOrder')} ${orderName} (${formatBindingLabel(hotkeyMap.packer_copy)})`}
              >
                {copied ? (
                  <ClipboardCheck className="h-5 w-5 text-green-600" aria-hidden="true" />
                ) : (
                  <Copy className="h-5 w-5" aria-hidden="true" />
                )}
                {copied ? t('shipping.copied') : t('shipping.copyOrder')}
                <kbd className="text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_copy)}</kbd>
              </Button>

              {!allItemsVerified && (
                <p className="text-sm text-muted-foreground text-center">
                  {t('shipping.verifyBeforeReady')}
                </p>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-3">
            {canUseLabelProvider ? (
              <>
                <Button
                  data-packing-primary
                  onClick={handleSendToShipping}
                  disabled={!allItemsVerified || sendMutation.isPending}
                  className="w-full h-16 text-lg font-semibold gap-3"
                  size="lg"
                  aria-label={
                    allItemsVerified
                      ? `${t('shipping.printShippingLabel')} (${formatBindingLabel(hotkeyMap.packer_ship)})`
                      : `${t('shipping.printShippingLabel')} - ${t('shipping.verifyBeforeShipping')}`
                  }
                >
                  <Printer className="h-6 w-6" aria-hidden="true" />
                  {t('shipping.printShippingLabel')}
                  <kbd className="ml-1 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_ship)}</kbd>
                </Button>

                <div className="flex gap-2">
                  <Button
                    data-packing-manual
                    onClick={handleManualComplete}
                    disabled={!allItemsVerified}
                    variant="outline"
                    className="flex-1 h-12 text-base font-medium gap-2"
                    size="lg"
                    aria-label={`${t('shipping.completeManually')} (${formatBindingLabel(hotkeyMap.packer_manual)})`}
                  >
                    <Package className="h-5 w-5" aria-hidden="true" />
                    {t('shipping.completeManually')}
                    <kbd className="ml-auto text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_manual)}</kbd>
                  </Button>

                  <Button
                    data-packing-copy
                    onClick={handleCopyOrderNumber}
                    variant="outline"
                    className="h-12 px-4 text-base font-medium gap-2"
                    size="lg"
                    aria-label={`${t('shipping.copyOrder')} ${orderName} (${formatBindingLabel(hotkeyMap.packer_copy)})`}
                  >
                    {copied ? (
                      <ClipboardCheck className="h-5 w-5 text-green-600" aria-hidden="true" />
                    ) : (
                      <Copy className="h-5 w-5" aria-hidden="true" />
                    )}
                    {copied ? t('shipping.copied') : t('shipping.copyOrder')}
                    <kbd className="text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_copy)}</kbd>
                  </Button>
                </div>

                {isStarShipIt && (
                  <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
                    <span>{t('shipping.orPrintInStarshipit')}</span>
                    <a
                      href={starshipitUrl(orderName)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-primary hover:text-primary/80 underline underline-offset-2"
                    >
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      {t('shipping.viewInStarshipit')}
                    </a>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <Button
                  data-packing-primary
                  onClick={handleManualComplete}
                  disabled={!allItemsVerified}
                  className="w-full h-16 text-lg font-semibold gap-3"
                  variant="default"
                  size="lg"
                  aria-label={
                    allItemsVerified
                      ? `${t('shipping.markComplete')} (${formatBindingLabel(hotkeyMap.packer_ship)})`
                      : `${t('shipping.markComplete')} - ${t('shipping.verifyBeforeShipping')}`
                  }
                >
                  <Check className="h-6 w-6" aria-hidden="true" />
                  {t('shipping.markComplete')}
                  <kbd className="ml-1 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_ship)}</kbd>
                </Button>

                <Button
                  data-packing-copy
                  onClick={handleCopyOrderNumber}
                  variant="outline"
                  className="w-full h-12 text-base font-medium gap-2"
                  size="lg"
                  aria-label={`${t('shipping.copyOrder')} ${orderName} (${formatBindingLabel(hotkeyMap.packer_copy)})`}
                >
                  {copied ? (
                    <ClipboardCheck className="h-5 w-5 text-green-600" aria-hidden="true" />
                  ) : (
                    <Copy className="h-5 w-5" aria-hidden="true" />
                  )}
                  {copied ? t('shipping.copied') : t('shipping.copyOrder')}
                  <kbd className="text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_copy)}</kbd>
                </Button>
              </div>
            )}

            {!allItemsVerified && (
              <p className="text-sm text-muted-foreground text-center">
                {t('shipping.verifyBeforeShipping')}
              </p>
            )}

            {packingCopy && !hasCredentials && (
              <Alert>
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <AlertDescription>{t(packingCopy.notConfigured)}</AlertDescription>
              </Alert>
            )}
          </div>
        );

      case 'loading':
        return (
          <div
            className="flex flex-col items-center justify-center py-8 space-y-4"
            role="status"
            aria-label={
              sendMutation.isPending
                ? t('shipping.generatingLabel')
                : t('processing')
            }
          >
            <Loader2 className="h-12 w-12 animate-spin text-primary" aria-hidden="true" />
            <p className="text-lg font-medium" aria-hidden="true">
              {sendMutation.isPending ? t('shipping.generatingLabel') : t('processing')}
            </p>
          </div>
        );

      case 'success':
        return (
          <Card className={cn(
            'relative',
            alreadyShipped ? 'border-blue-500 border-2' : 'border-green-500 border-2',
          )}>
            {/* Close button — BLU-282: for StarShipIt, dismiss = move on (order already shipped) */}
            <button
              onClick={() => {
                if (canUseLabelProvider) {
                  onShipmentComplete({ manual: false });
                } else {
                  setPanelState('ready');
                  setShippingResult(null);
                  setAlreadyShipped(false);
                  setLabelPrinted(false);
                }
              }}
              className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-muted transition-colors z-10"
              aria-label={t('common:close')}
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>

            <CardContent className="pt-6 space-y-4">
              <Alert variant="default" className={alreadyShipped ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}>
                <Check className={alreadyShipped ? 'h-5 w-5 text-blue-600' : 'h-5 w-5 text-green-600'} aria-hidden="true" />
                <AlertTitle className={alreadyShipped ? 'text-blue-800' : 'text-green-800'}>
                  {alreadyShipped ? t('shipping.labelAlreadyTitle') : t('shipping.labelGeneratedTitle')}
                </AlertTitle>
                <AlertDescription className={alreadyShipped ? 'text-blue-700' : 'text-green-700'}>
                  <div className="mt-2 space-y-1">
                    {shippingResult?.trackingNumber && (
                      <p className="font-mono text-lg font-semibold break-all">
                        {shippingResult.trackingNumber}
                      </p>
                    )}
                    {shippingResult?.carrierName && (
                      <p>{t('shipping.carrier')} {shippingResult.carrierName}</p>
                    )}
                    {/* Each provider says how to get its label back once the
                        order has shipped. Portless and YunExpress re-entries
                        carry no tracking or label in hand, so without this
                        line the success card body would be completely empty. */}
                    {alreadyShipped && packingCopy && (
                      <p className="text-sm mt-1">
                        {t(packingCopy.alreadyShipped)}
                      </p>
                    )}
                    {isPortless && !alreadyShipped && !shippingResult?.trackingNumber && (
                      <p className="text-sm mt-1">
                        {t('shipping.portlessLabelReceived')}
                      </p>
                    )}
                    {isStarShipIt && (
                      <a
                        href={starshipitUrl(orderName)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'inline-flex items-center gap-1.5 text-sm font-medium mt-2 underline underline-offset-2',
                          alreadyShipped ? 'text-blue-700 hover:text-blue-900' : 'text-green-700 hover:text-green-900'
                        )}
                      >
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('shipping.viewInStarshipit')}
                      </a>
                    )}
                  </div>
                </AlertDescription>
              </Alert>

              {/* Error message for confirm failures */}
              {errorMessage && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" aria-hidden="true" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              {/* No label data notice — user must print from StarShipIt
                  (StarShipIt-only: a successful Portless pull always carries
                  the label PDF) */}
              {!shippingResult?.labelBase64 && !alreadyShipped && isStarShipIt && (
                <Alert>
                  <Printer className="h-4 w-4" aria-hidden="true" />
                  <AlertDescription>
                    {t('shipping.labelNotIncluded')}
                  </AlertDescription>
                </Alert>
              )}

              {/* BLU-282: label provider auto-shipped — show print button + status message only */}
              {canUseLabelProvider && shippingResult ? (
                <div className="space-y-3">
                  {(shippingResult.labelBase64 || isStarShipIt) && (
                  <Button
                    data-packing-print-label
                    onClick={async () => {
                      if (shippingResult?.labelBase64) {
                        const result = await agentPrintLabel({
                          data: shippingResult.labelBase64,
                          format: shippingResult.labelType || 'PDF',
                        });
                        if (result.success) {
                          toast.success(t('shipping.labelSentToPrinter'));
                          setLabelPrinted(true);
                          // Stay on Reprint state so worker can verify print came out
                          // and reprint if needed. Ctrl+Enter, X, or scan next dismisses.
                        } else {
                          setShowPrintDialog(true);
                        }
                      } else {
                        window.open(starshipitUrl(orderName), '_blank', 'noopener,noreferrer');
                      }
                    }}
                    variant={labelPrinted ? 'outline' : 'default'}
                    className="w-full h-16 text-lg font-medium gap-2"
                    aria-label={
                      shippingResult?.labelBase64
                        ? (labelPrinted ? `${t('shipping.reprintLabel')} (${formatBindingLabel(hotkeyMap.packer_label)})` : `${t('shipping.printLabel')} (${formatBindingLabel(hotkeyMap.packer_label)})`)
                        : t('shipping.printInStarshipit')
                    }
                  >
                    <Printer className="h-6 w-6" aria-hidden="true" />
                    {shippingResult?.labelBase64
                      ? (labelPrinted ? t('shipping.reprintLabel') : t('shipping.printLabel'))
                      : t('shipping.printInStarshipit')}
                    <kbd className="ml-1 text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_label)}</kbd>
                  </Button>
                  )}

                  {/* No label in hand (already-shipped re-entry) and the
                      provider can fetch it again by order: Portless re-pulls
                      the PDF from Pipe17, YunExpress re-downloads the stored
                      waybill's label. Read-only, status unchanged. Carries
                      data-packing-print-label so the print hotkey works; the
                      main print button is hidden in this branch so the
                      attribute is unique. */}
                  {(reprintMode === 'portless' || reprintMode === 'api') && !shippingResult.labelBase64 && (
                    <Button
                      data-packing-print-label
                      onClick={handleReprint}
                      disabled={reprintPending}
                      aria-busy={reprintPending}
                      variant="default"
                      className="w-full h-16 text-lg font-medium gap-2"
                      aria-label={`${t('shipping.reprintLabel')} (${formatBindingLabel(hotkeyMap.packer_label)})`}
                    >
                      {reprintPending ? (
                        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
                      ) : (
                        <Printer className="h-6 w-6" aria-hidden="true" />
                      )}
                      {t('shipping.reprintLabel')}
                      <kbd className="ml-1 text-[10px] font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_label)}</kbd>
                    </Button>
                  )}

                  {/* Status message — amber when label needs printing, green when printed.
                      "Didn't auto-print" is only true when a label was in hand
                      and the print agent failed on it. An already-shipped
                      reopen with no label never tried to print, and the alert
                      above already says how to get the label back, so it
                      shows nothing here until a reprint succeeds. */}
                  {(labelPrinted || !alreadyShipped || Boolean(shippingResult.labelBase64)) && (
                    <div className={cn(
                      'flex items-center justify-center gap-2 py-2 text-sm',
                      labelPrinted ? 'text-green-700' : 'text-amber-700'
                    )}>
                      <Check className="h-4 w-4" aria-hidden="true" />
                      <span>{labelPrinted
                        ? t('shipping.orderShippedScanNext')
                        : t('shipping.labelNotAutoPrinted')
                      }</span>
                    </div>
                  )}
                </div>
              ) : (
                /* Non-StarShipIt: existing 2-column grid */
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    data-packing-print-label
                    onClick={async () => {
                      if (shippingResult?.labelBase64) {
                        const result = await agentPrintLabel({
                          data: shippingResult.labelBase64,
                          format: shippingResult.labelType || 'PDF',
                        });
                        if (result.success) {
                          toast.success(t('shipping.labelSentToPrinter'));
                          setLabelPrinted(true);
                        } else {
                          setShowPrintDialog(true);
                        }
                      } else {
                        window.open(starshipitUrl(orderName), '_blank', 'noopener,noreferrer');
                      }
                    }}
                    variant="outline"
                    className="h-16 text-lg font-medium gap-2"
                    disabled={confirmMutation.isPending}
                    aria-label={
                      shippingResult?.labelBase64
                        ? (labelPrinted ? `${t('shipping.reprintLabel')} (${formatBindingLabel(hotkeyMap.packer_label)})` : `${t('shipping.printLabel')} (${formatBindingLabel(hotkeyMap.packer_label)})`)
                        : t('shipping.printInStarshipit')
                    }
                  >
                    <Printer className="h-6 w-6" aria-hidden="true" />
                    {shippingResult?.labelBase64
                      ? (labelPrinted ? t('shipping.reprintLabel') : t('shipping.printLabel'))
                      : t('shipping.printInStarshipit')}
                    <kbd className="ml-1 text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_label)}</kbd>
                  </Button>

                  {shippingResult?.trackingNumber ? (
                    <Button
                      ref={confirmButtonRef}
                      onClick={handleMarkAsShipped}
                      disabled={confirmMutation.isPending}
                      aria-busy={confirmMutation.isPending}
                      aria-label={
                        confirmMutation.isPending
                          ? `${t('shipping.shippingDots')}`
                          : `${t('shipping.markAsShipped')} (${formatBindingLabel(hotkeyMap.packer_ship)})`
                      }
                      className="h-16 text-lg font-semibold gap-2"
                    >
                      {confirmMutation.isPending ? (
                        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
                      ) : (
                        <Package className="h-6 w-6" aria-hidden="true" />
                      )}
                      {confirmMutation.isPending ? t('shipping.shippingDots') : t('shipping.markAsShipped')}
                      <kbd className="ml-1 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_confirm)}</kbd>
                    </Button>
                  ) : (
                    <Button
                      ref={confirmButtonRef}
                      data-packing-manual
                      onClick={handleManualComplete}
                      disabled={completePackingMutation.isPending}
                      aria-busy={completePackingMutation.isPending}
                      aria-label={t('shipping.completeManually')}
                      variant="secondary"
                      className="h-16 text-lg font-medium gap-2"
                    >
                      {completePackingMutation.isPending ? (
                        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
                      ) : (
                        <Check className="h-6 w-6" aria-hidden="true" />
                      )}
                      {t('shipping.completeManually')}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );

      case 'error': {
        // A classified failure carries its own "what to do" in the sentence,
        // so the message-sniffing guidance line is only for unclassified ones
        // (and can no longer say "a coordinator will review" next to a
        // retryable error that was deliberately not queued).
        const described = codedError
          ? describeYunExpressError((key, params) => t(key, params), codedError, errorMessage ?? '')
          : null;
        const errorCategory = errorMessage ? categoriseError(errorMessage) : 'validation';
        const guidance = described
          ? null
          : getErrorGuidance(errorCategory, t, packingCopy?.checkCredentials ?? null, isStarShipIt);
        return (
          <div className="space-y-4">
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-5 w-5" aria-hidden="true" />
              <AlertTitle>{t('shipping.shippingFailedTitle')}</AlertTitle>
              <AlertDescription>
                <p className="text-base">{described?.message ?? errorMessage}</p>
                {described?.detail && (
                  <p className="mt-1 break-words text-sm text-muted-foreground">
                    {described.detail}
                  </p>
                )}
                {providerErrors && providerErrors.length > 0 && (
                  <ul className="mt-2 list-disc list-inside text-sm space-y-0.5">
                    {providerErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                )}
                {guidance && (
                  <p className="mt-1 text-sm text-destructive/90">
                    {guidance}
                  </p>
                )}
                {!described && errorCategory === 'validation' && isStarShipIt && (
                  <a
                    href={starshipitUrl(orderName)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium mt-2 underline underline-offset-2 text-destructive hover:text-destructive/80"
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('shipping.fixInStarshipit')}
                  </a>
                )}
                {failureQueued && (
                  <p className="mt-1 text-sm text-destructive/90">
                    {t('shipping.addedToCoordinatorQueue')}
                  </p>
                )}
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => {
                  setPanelState('ready');
                  setErrorMessage(null);
                  setCodedError(null);
                  setProviderErrors(null);
                  setFailureQueued(false);
                }}
                variant="outline"
                className="h-16 text-lg font-medium gap-2"
                aria-label={t('shipping.tryAgain')}
              >
                <RefreshCw className="h-6 w-6" aria-hidden="true" />
                {t('shipping.tryAgain')}
              </Button>

              <Button
                data-packing-manual
                onClick={handleManualComplete}
                disabled={completePackingMutation.isPending}
                aria-busy={completePackingMutation.isPending}
                aria-label={
                  completePackingMutation.isPending
                    ? t('shipping.completing')
                    : `${t('shipping.completeManually')} (${formatBindingLabel(hotkeyMap.packer_manual)})`
                }
                variant="secondary"
                className="h-16 text-lg font-medium gap-2"
              >
                {completePackingMutation.isPending ? (
                  <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
                ) : (
                  <Check className="h-6 w-6" aria-hidden="true" />
                )}
                {t('shipping.completeManually')}
                <kbd className="ml-1 text-[10px] font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_manual)}</kbd>
              </Button>
            </div>
          </div>
        );
      }
    }
  };

  return (
    <>
      <div className="w-full" role="region" aria-label={t('shipping.actionsRegion')}>
        {renderContent()}
      </div>

      {/* Print dialog */}
      {shippingResult && (
        <LabelPrintDialog
          open={showPrintDialog}
          onOpenChange={setShowPrintDialog}
          labelBase64={shippingResult.labelBase64}
          labelType={shippingResult.labelType}
          trackingNumber={shippingResult.trackingNumber}
          carrier={
            shippingResult.carrierName || shippingResult.carrier || 'Unknown'
          }
          orderName={orderName}
          isReprint={labelPrinted}
          onPrintComplete={() => {
            setShowPrintDialog(false);
            setLabelPrinted(true);
            if (confirmAfterPrintRef.current) {
              // Triggered from "Mark as Shipped" — auto-confirm after print
              confirmAfterPrintRef.current = false;
              handleConfirmShipment();
            } else if (canUseLabelProvider) {
              // BLU-282: Order already shipped on backend — dismiss panel after print
              onShipmentComplete({ manual: false });
            }
            // BLU-286: Don't focus confirm button after print — USB scanner Enter
            // would activate it. Users use Ctrl+Enter or click instead.
          }}
        />
      )}

      {/* ARIA live region for announcements - polite to avoid interrupting */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>
    </>
  );
}
